"use client";

import { useEffect } from "react";
import AppImage from "@/components/ui/AppImage";

interface Equipment {
  id: string;
  title: string;
  category: string;
  image: string;
  alt: string;
  description: string;
}

export default function EquipmentGrid() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const equipment: Equipment[] = [
  {
    id: "equipment_1",
    title: "Professional Cinema Cameras",
    category: "Cinematography",
    image: "https://images.unsplash.com/photo-1705107958338-7f9d0ecb8e63",
    alt: "Professional cinema camera with lens on tripod in studio setting",
    description: "4K/8K cinema cameras with full lens kits"
  },
  {
    id: "equipment_2",
    title: "Studio Lighting Systems",
    category: "Lighting",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0b4e693-1769170457858.png",
    alt: "Professional studio lighting equipment with softboxes and LED panels",
    description: "Complete lighting setups for any production"
  },
  {
    id: "equipment_3",
    title: "Audio Recording Suite",
    category: "Sound",
    image: "https://images.unsplash.com/photo-1695988549009-b994fa743124",
    alt: "Professional audio recording studio with mixing console and monitors",
    description: "State-of-the-art sound recording and mixing"
  },
  {
    id: "equipment_4",
    title: "Editing Workstations",
    category: "Post-Production",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3a43fd0-1767406344190.png",
    alt: "Professional video editing workstation with multiple monitors and color grading software",
    description: "High-performance editing and color grading suites"
  }];


  return (
    <section id="equipment" className="py-24 md:py-32 px-6 md:px-12 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-display font-light text-white mb-4">
            Professional Equipment & Facilities
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light">
            State-of-the-art technology and facilities for exceptional production quality.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {equipment.map((item, index) =>
          <div
            key={item.id}
            className={`group relative h-[400px] rounded-3xl overflow-hidden glass-card transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl animate-on-scroll [animation:animationIn_0.8s_ease-out_${
            0.3 + index * 0.1}s_both]`
            }>
            
              <div className="absolute inset-0">
                <AppImage
                src={item.image}
                alt={item.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent"></div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="inline-block px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-xs font-medium text-primary mb-3 uppercase tracking-wider">
                  {item.category}
                </span>
                <h3 className="text-2xl font-display font-medium text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 text-center animate-on-scroll [animation:animationIn_0.8s_ease-out_0.6s_both]">
          <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-all">
            Schedule a Facility Tour
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7" />
              
            </svg>
          </button>
        </div>
      </div>
    </section>);

}